import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Container } from '@mui/material';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import Login from './pages/Login';
import Overview from './pages/Overview';
import Achievements from './pages/Achievements';
import Insights from './pages/Insights';
import CarbonImpact from './pages/CarbonImpact';
import SeasonalInsights from './pages/SeasonalInsights';
import EfficiencyMetrics from './pages/EfficiencyMetrics';

// Services
import { getBusinessId, clearToken as logout, isAuthenticated } from './services/auth';

const App = () => {
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated()) {
        const storedBusinessId = getBusinessId();
        setBusinessId(storedBusinessId);
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Function to handle login
  const handleLogin = (id: string) => {
    setBusinessId(id);
  };

  // Function to handle logout
  const handleLogout = () => {
    logout();
    setBusinessId(null);
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          bgcolor: 'background.default'
        }}
      >
        Loading...
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      {businessId && <Header 
        businessId={businessId} 
        onLogout={handleLogout} 
      />}
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          py: businessId ? 3 : 0,
          display: 'flex',
          position: 'relative',
          zIndex: 1
        }}
      >
        {businessId ? (
          <Container maxWidth="lg">
            <Routes>
              {/* Public route */}
              <Route 
                path="/" 
                element={<Navigate to="/overview" />} 
              />

              {/* Protected routes */}
              <Route 
                path="/overview" 
                element={<Overview businessId={businessId} />} 
              />
              <Route 
                path="/achievements" 
                element={<Achievements businessId={businessId} />} 
              />
              <Route 
                path="/insights" 
                element={<Insights businessId={businessId} />} 
              />
              <Route 
                path="/carbon-impact" 
                element={<CarbonImpact businessId={businessId} />} 
              />
              <Route 
                path="/seasonal-insights" 
                element={<SeasonalInsights />} 
              />
              <Route 
                path="/efficiency-metrics" 
                element={<EfficiencyMetrics />} 
              />

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Container>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Routes>
              <Route path="/" element={<Login onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Box>
        )}
      </Box>

      {businessId && <Footer />}
    </Box>
  );
};

export default App; 